import cx from 'classnames';
import { useAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Sticky from 'react-sticky-el';

import useIsMobile from '../../../hooks/use-is-mobile';
import useLinkProps from '../../../hooks/use-link-props';
import { getBrandHistory } from '../../../services/brand-history';
import { cartAtom, cartOpen } from '../../../state/cart';
import { getExpressshipping } from '../../../utils/get-express-shipping';
import Help from '../../modules/help';
import Reviews from '../../modules/reviews';
import Trustpilot from '../../modules/trustpilot';
import BrandHistory from '../../modules/v2/brand-history';
import Breadcrumbs from '../../modules/v2/breadcrumbs';
import { useAppContext } from '../../providers/app-context';
import Button from '../../ui/button';
import Chevrelon from '../../ui/icons/cheverlon';
import BPImage from '../../ui/image';
import LazyHydration from '../../ui/lazy-hydration';
import TechInfo from '../../ui/vehicle/vehicle-technical-information';

import AddToCart from './add-to-cart';
import BPartsPurchaseInfo from './b-parts-purchase-info';
import BeforeContent from './before-content';
import useBreadcrumbItems from './breadcrumbItems';
import MoreInfo from './more-info';
import MoreParts from './more-parts';
import Observation from './observation';
import PartInfo from './part-info';
import PriceInfo from './price-info';
import ProductTitle, {
  currentSelectionAtom,
  getInheritContent,
} from './product-title';
import classes from './product.module.css';
import RelatedProducts from './related-products';
import ProductSeo from './seo';
import SEOContent from './seo-content-section';
import ProductPhotos from './v2/photos';
import EcoRepairScoreModule from '../../modules/eco-repair-score';

const EuropeAssitanceBanner = dynamic(
  () => import('./top-banners/europ-assistance')
);

const Product: FunctionComponent<TPageProduct> = ({
  productDetail,
  refetchProductDetail,
  relatedProducts,
  uniqueVehicle,
  categoriesInfo,
  translatedSlugs,
}) => {
  const { t } = useTranslation();
  const refImage = useRef();
  const sectionsRef = useRef([]);
  const { push, locale = 'en' } = useRouter();
  const [hasAdded, sethasAdded] = useState(false); // added while viewing product
  const [isImageVisible, setIsImageVisible] = useState(false);
  const [shoppingCart] = useAtom(cartAtom);
  const breadcrumbItems = useBreadcrumbItems({ productDetail, categoriesInfo });
  const isSold = productDetail.status === 'SOL';
  const isReservated = productDetail.status === 'RSV';
  const isMobile = useIsMobile();
  const [open] = useAtom(cartOpen);
  const getLink = useLinkProps();
  const { isBparts } = useAppContext();

  const [currentSelection, setCurrentSelection] = useAtom(currentSelectionAtom);

  const brandHistory = getBrandHistory({
    locale: locale,
    slug: productDetail?.car.brand_alias,
  });

  const maxNumberOfSections = useMemo(() => {
    return uniqueVehicle ? 5 : 4;
  }, [uniqueVehicle]);

  const intersectionCallback = useCallback((allSectionsRef) => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('id');
            setCurrentSelection(id);
          }
        });
      },
      {
        // threshold: 0.5,
        rootMargin: '-148px 0px -980px 0px',
      }
    );
    allSectionsRef.forEach((section) => {
      observer.observe(section);
    });
  }, []);

  const refCallback = useCallback(
    (element) => {
      if (element) {
        sectionsRef.current.push(element);
        if (sectionsRef.current.length === maxNumberOfSections) {
          intersectionCallback(sectionsRef.current);
        }
      }
    },
    [maxNumberOfSections]
  );

  useEffect(() => {
    (window as any).dataLayer.push({
      event: 'productAvailability',
      productAvailability: isSold ? 'outOfStock' : 'inStock', // product availability = inStock or outOfStock
    });

    (window as any).intercomSettings = {
      ...(window as any).intercomSettings,
      last_page_value: productDetail?.pricing?.price_final || null,
      last_product_status: productDetail?.status,
      last_part_type: productDetail?.part_en,
    };
  }, []);

  useEffect(() => {
    if (productDetail.in_cart && !shoppingCart.includes(productDetail.id)) {
      sethasAdded(false);
      productDetail.in_cart = false;
      //refetchProductDetail();
    }
    if (!productDetail.in_cart && shoppingCart.includes(productDetail.id)) {
      sethasAdded(true);
      productDetail.in_cart = true;
      //refetchProductDetail();
    }
  }, [shoppingCart, open]);

  const hasEurop = [51, 53, 54].includes(productDetail.part.id);

  return (
    <div className="PPContent">
      <ProductSeo
        product={productDetail}
        breadcrumbs={breadcrumbItems}
        translatedSlugs={translatedSlugs}
      />
      <div
        className={cx({
          'bg-red-100': isSold,
          'bg-orange-100': !isSold && !hasAdded && productDetail.in_cart,
          'bg-green-40': !isSold && hasAdded,
        })}
      >
        <LazyHydration
          Component={Breadcrumbs}
          items={breadcrumbItems}
          currentPage={productDetail.sku}
        />
      </div>

      {!isSold && (
        <>
          <Sticky
            mode="top"
            topOffset={0}
            boundaryElement=".PPContent"
            hideOnBoundaryHit={false}
            wrapperClassName={cx(
              'relative w-full mx-auto !z-20',
              'pt-6',
              isReservated
                ? 'bg-yellow-10'
                : isSold
                  ? 'bg-red-10'
                  : productDetail.in_cart
                    ? 'bg-green-10'
                    : 'bg-neutral-5',
              'border-neutral-5 lg:shadow max-lg:pr-4',
              '[&.sticky>section>section>section>div#h2_sku>div]:hidden'
            )}
            stickyClassName={cx('max-lg:sticky')}
            dontUpdateHolderHeightWhenSticky={true}
          >
            <div
              className={cx(
                ' w-full container mx-auto justify-between items-end gap-4 grid',
                isReservated
                  ? 'bg-yellow-10'
                  : isSold
                    ? 'bg-red-10'
                    : productDetail.in_cart
                      ? 'bg-green-10'
                      : 'bg-neutral-5',
                isImageVisible
                  ? 'grid-cols-1 lg:grid-cols-[auto_21fr]'
                  : 'grid-cols-1'
              )}
            >
              {isImageVisible && (
                <div className="max-lg:hidden h-auto min-w-[200px] duration-400 animate-slide-down">
                  <div
                    className={cx(
                      'relative',
                      'w-full aspect-video',
                      'lg:w-auto lg:h-[calc(100%)]'
                    )}
                  >
                    <BPImage
                      src={
                        productDetail?.images[0].image_big ||
                        `${process.env.PUBLIC_PREFIX}/svg/placeholder.svg`
                      }
                      layout="fill"
                      objectFit="cover"
                      objectPosition="center"
                      alt="product main image"
                      priority
                    />
                  </div>
                </div>
              )}
              <div className={'w-full'}>
                <ProductTitle
                  productDetail={productDetail}
                  isSold={isSold}
                  isReservated={isReservated}
                  isImageVisible={isImageVisible}
                >
                  <PriceInfo
                    productDetail={productDetail}
                    refetchProductDetail={refetchProductDetail}
                    hasEurop={hasEurop}
                    isSold={isSold}
                  />
                </ProductTitle>
                <div className="max-lg:hidden container mx-auto w-full flex flex-row gap-4 justify-between items-end pt-4 lg:grid lg:grid-cols-[6fr_2fr]">
                  <div className="flex flew-row w-fit h-fit max-lg:gap-4 overflow-y-auto">
                    {getInheritContent(
                      t,
                      {
                        vehicle: uniqueVehicle && uniqueVehicle.stock > 0 ? uniqueVehicle : null,
                        tech_info: productDetail?.technical_info,
                      },
                      currentSelection,
                      setCurrentSelection,
                      isSold,
                      isReservated,
                      productDetail?.in_cart
                    ).map((t, index) => {
                      return (
                        <div
                          key={t?.key}
                          className="flex grow min-w-fit flex-nowrap"
                        >
                          {t?.tab}
                        </div>
                      );
                    })}
                  </div>
                  <AddToCart
                    productDetail={productDetail}
                    refetchProductDetail={refetchProductDetail}
                    isImageVisible={isImageVisible}
                  ></AddToCart>
                </div>
              </div>
            </div>
          </Sticky>
        </>
      )}
      {hasEurop && <EuropeAssitanceBanner />}
      {isSold && (
        <BeforeContent
          productDetail={productDetail}
          hasAdded={hasAdded}
          relatedProducts={relatedProducts}
        />
      )}
      {isSold && (
        <div
          className={cx(
            ' w-full shadow',
            isReservated
              ? 'bg-yellow-10'
              : isSold
                ? 'bg-red-10'
                : productDetail.in_cart
                  ? 'bg-green-10'
                  : productDetail.in_cart
                    ? 'bg-green-10'
                    : 'bg-neutral-5',
            isImageVisible
              ? 'grid-cols-1 lg:grid-cols-[auto_21fr]'
              : 'grid-cols-1'
          )}
        >
          <div className="w-full container mx-auto pt-6">
            <ProductTitle productDetail={productDetail} isSold={isSold}>
              <PriceInfo
                productDetail={productDetail}
                hasEurop={hasEurop}
                isSold={isSold}
              />
            </ProductTitle>
            <div className="max-lg:hidden container mx-auto w-full flex flex-row gap-4 justify-between items-end pt-4 lg:grid lg:grid-cols-[6fr_2fr]">
              <div className="flex flew-row w-fit h-fit max-lg:gap-4 overflow-y-auto">
                {getInheritContent(
                  t,
                  {
                    vehicle: uniqueVehicle,
                    tech_info: productDetail?.technical_info,
                  },
                  currentSelection,
                  setCurrentSelection,
                  isSold,
                  isReservated,
                  productDetail?.in_cart
                ).map((t, index) => {
                  return (
                    <div
                      key={t?.key}
                      className="flex grow min-w-fit flex-nowrap"
                    >
                      {t?.tab}
                    </div>
                  );
                })}
              </div>
              <AddToCart
                productDetail={productDetail}
                isImageVisible={isImageVisible}
              ></AddToCart>
            </div>
          </div>
        </div>
      )}

      <div
        className={cx(
          'lg:container lg:mx-auto',
          'lg:py-12',
          'grid',
          'lg:grid-cols-[2fr_2fr] lg:gap-12 ',
          'lg:bg-gradient-to-rmb-9',
          classes.outerContainer
        )}
      >
        <section
          className={cx(
            'relative max-lg:pt-0 max-lg:pb-2 max-md:px-4',
            isSold
              ? 'max-lg:bg-red-10'
              : isReservated
                ? 'max-lg:bg-yellow-10'
                : productDetail.in_cart
                  ? 'max-lg:bg-green-10'
                  : 'max-lg:bg-neutral-5',
            classes.imagesContainer
          )}
        >
          <div
            className={cx(
              'max-lg:container max-lg:mx-auto',
              isSold
                ? 'lg:sticky lg:top-[calc(48px+24px)]'
                : 'lg:sticky lg:top-[calc(160px+24px)]'
            )}
          >
            <div ref={refImage} className="">
              <ProductPhotos
                list={productDetail.images}
                modalTitle={
                  <>
                    <b>{productDetail.part.name}</b>{' '}
                    <small>{`${productDetail.car.brand_name} ${productDetail.car.model_name
                      } ${productDetail.car.version_name || ''}`}</small>{' '}
                    | <small>{productDetail.sku}</small>
                  </>
                }
                border={true}
                altExtraInfo={productDetail.sku}
                expressShipping={getExpressshipping(
                  productDetail.delivery_day_start,
                  productDetail.delivery_day_end
                )}
                steering_wheel_side={productDetail.steering_wheel_side}
                status={productDetail.status}
                isPP={true}
              />
            </div>
            <div className='max-lg:hidden lg:pt-8'>
              <EcoRepairScoreModule></EcoRepairScoreModule>
            </div>
            <aside className={cx('hidden', 'lg:block', 'mt-8')}>
              <Help />
            </aside>
          </div>
        </section>
        {isMobile && (
          <>
            {!isSold ? (
              <Sticky
                mode="top"
                topOffset={-80}
                boundaryElement=".PPContent"
                hideOnBoundaryHit={false}
                wrapperClassName={cx(
                  'lg:hidden relative max-lg:block w-full !z-30 pt-2 ',
                  isSold
                    ? 'max-lg:bg-red-10'
                    : isReservated
                      ? 'max-lg:bg-yellow-10'
                      : productDetail.in_cart
                        ? 'max-lg:bg-green-10'
                        : 'max-lg:bg-neutral-5',
                  'shadow-[0px_5px_8px_-2px_rgba(0,0,0,0.1)] pb-4 '
                )}
                stickyClassName={cx('mt-[80px] sticky', classes.isSticky)}
                dontUpdateHolderHeightWhenSticky={true}
              >
                <div className="flex flex-col gap-4 max-lg:container max-lg:mx-auto">
                  <PriceInfo
                    productDetail={productDetail}
                    hasEurop={hasEurop}
                    isSold={isSold}
                  />
                  <AddToCart
                    productDetail={productDetail}
                  ></AddToCart>
                </div>
              </Sticky>
            ) : (
              <div
                className={cx(
                  'lg:hidden max-lg:block w-full pt-4 max-lg:container max-lg:mx-auto',
                  isSold
                    ? 'max-lg:bg-red-10'
                    : isReservated
                      ? 'max-lg:bg-yellow-10'
                      : productDetail.in_cart
                        ? 'max-lg:bg-green-10'
                        : 'max-lg:bg-neutral-5'
                )}
              >
                <PriceInfo
                  productDetail={productDetail}
                  hasEurop={hasEurop}
                  isSold={isSold}
                />
              </div>
            )}
          </>
        )}
        <section className={cx('grid lg:gap-6', classes.rightContainer)}>
          <section
            className={cx(
              'grid gap-4 max-lg:gap-8 grid-cols-1 lg:grid-cols-[2fr_2fr] justify-between'
            )}
          >
            <div
              className={cx(
                'block lg:hidden',
                isSold
                  ? 'max-lg:bg-red-10'
                  : isReservated
                    ? 'max-lg:bg-yellow-10'
                    : productDetail.in_cart
                      ? 'max-lg:bg-green-10'
                      : 'max-lg:bg-neutral-5'
              )}
            >
              <BPartsPurchaseInfo
                productDetail={productDetail}
                hasEurop={hasEurop}
                isSold={isSold}
                isReservated={isReservated}
              ></BPartsPurchaseInfo>

              <div className='lg:hidden pb-4 px-4'>
                <EcoRepairScoreModule></EcoRepairScoreModule>
              </div>
            </div>

            <PartInfo
              productDetail={productDetail}
              key={'pp_details'}
              ref={refCallback}
            />
            {!isMobile && (
              <BPartsPurchaseInfo
                productDetail={productDetail}
                refetchProductDetail={refetchProductDetail}
                hasEurop={hasEurop}
              ></BPartsPurchaseInfo>
            )}
          </section>

          <Observation
            observations={productDetail.observations}
            observations_original={productDetail.observations_original}
            observations_original_lang={
              productDetail.observations_original_lang
            }
            observations_internal={productDetail.observations_internal}
            key={'pp_observations'}
            ref={refCallback}
          />
          <TechInfo
            technical_info={productDetail.technical_info}
            key={'pp_tec_info'}
            ref={refCallback}
          ></TechInfo>
          <section
            className={cx(
              'bg-brand-10',
              'pt-6 max-lg:pt-4 max-lg:pb-4',
              'lg:pb-3'
            )}
          >
            <MoreInfo
              productDetail={productDetail}
              ref={refCallback}
            ></MoreInfo>
          </section>
        </section>
      </div>
      {uniqueVehicle &&
        uniqueVehicle.stock > 0 && (
          <MoreParts
            car={productDetail.car}
            vehicle={uniqueVehicle}
            warranty={productDetail.warranty}
            uniqueVehicleProducts={uniqueVehicle.products || []}
            key={'pp_see_vehicle'}
            ref={refCallback}
          />
        )}
      {!isSold && (
        <>
          {uniqueVehicle ? (
            <>
              <Reviews />
              {relatedProducts && (
                <>
                  <RelatedProducts relatedProducts={relatedProducts} />
                  <div className={cx('w-full  bg-neutral-10')}>
                    <div
                      className={cx(
                        'w-full container mx-auto flex flex-row items-center justify-center bg-neutral-10',
                        'text-center',
                        'pb-14',
                        {
                          'pt-14': !relatedProducts,
                        }
                      )}
                    >
                      <Button
                        variant="GHOST-PRIMARY"
                        isFullWidth={false}
                        className={cx(
                          'flex flex-row gap-6 items-center justify-center lg:justify-between',
                          isBparts && '!text-blue-100'
                        )}
                        onClick={() => {
                          const link = getLink('products', {
                            category: productDetail.part.subgroup_slug,
                            subCategory: productDetail.part.slug,
                            vehicle:
                              productDetail.car.version_slug ||
                              productDetail.car.model_slug,
                          });
                          push(link.href, link.as, { shallow: true });
                        }}
                      >
                        <>{t('common:search-all-parts')}</>
                        <Chevrelon
                          className={cx(
                            'w-4 h-4 -rotate-90',
                            isBparts ? 'text-blue-100' : 'text-brand-100'
                          )}
                        ></Chevrelon>
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              {relatedProducts && (
                <>
                  <RelatedProducts relatedProducts={relatedProducts} />
                  <div className={cx('w-full  bg-neutral-10')}>
                    <div
                      className={cx(
                        'w-full container mx-auto flex flex-row items-center justify-center bg-neutral-10',
                        'text-center',
                        'pb-14',
                        {
                          'pt-14': !relatedProducts,
                        }
                      )}
                    >
                      <Button
                        variant="GHOST-PRIMARY"
                        isFullWidth={false}
                        className={cx(
                          'flex flex-row gap-6 items-center justify-center lg:justify-between',
                          isBparts && '!text-blue-100'
                        )}
                        onClick={() => {
                          const link = getLink('products', {
                            category: productDetail.part?.subgroup_slug,
                            subCategory: productDetail.part?.slug,
                            vehicle:
                              productDetail.car?.version_slug ||
                              productDetail.car?.model_slug,
                          });
                          push(link.href, link.as, { shallow: true });
                        }}
                      >
                        <>{t('common:search-all-parts')}</>
                        <Chevrelon
                          className={cx(
                            'w-4 h-4 -rotate-90',
                            isBparts ? 'text-blue-100' : 'text-brand-100'
                          )}
                        ></Chevrelon>
                      </Button>
                    </div>
                  </div>
                </>
              )}
              <Reviews />
            </>
          )}
        </>
      )}

      {
        brandHistory && (
          <section className="w-full bg-blue-10 py-12 lg:py-16 text-justify ">
            <BrandHistory
              {...brandHistory}
              brand={productDetail?.car.brand_name}
              vehicle={{
                brand: {
                  name: productDetail?.car.brand_name,
                  slug: productDetail?.car.brand_slug,
                  alias: productDetail?.car.brand_alias,
                },
                model: {
                  name: productDetail?.car.model_name,
                  slug: productDetail?.car.model_slug,
                },
                version: {
                  name: productDetail?.car.version_name,
                  slug: productDetail?.car.version_slug,
                },
              }}
              stock={
                (productDetail?.car.brand_stock &&
                  productDetail?.car.brand_stock?.toLocaleString(locale)) ||
                ''
              }
            />
            <SEOContent productDetail={productDetail}></SEOContent>
          </section>
        )
      }

      <div className={cx({ 'lg:block bg-white': !isSold })}>
        <Trustpilot className={'py-8 lg:py-6'} backgroundColor="bg-white" />
      </div>
    </div >
  );
};

export default Product;
